// Tenantra Inc. - Needs-attention items grouped by category: shared loader, Home summary, bell dropdown, notifications page
import { useEffect, useState, useCallback } from 'react';
import { ArrowRight, AlertTriangle, FileSignature, CalendarClock, Banknote, Home, CheckCircle2, Bell, XCircle, UserCheck, AtSign } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { navigate } from '@/services/navigation';
import { useUrlParam } from '@/services/useUrlParam';
import { TenantPanel } from './TenantPanel';
import { loadHomeData, lastMonthStart, monthLabel } from './homeData';
import api from '@/services/api';

const TONE = {
  red: { text: 'text-red-600', dot: 'bg-red-500', icon: 'text-red-500 bg-red-50' },
  amber: { text: 'text-amber-700', dot: 'bg-amber-500', icon: 'text-amber-600 bg-amber-50' },
  slate: { text: 'text-slate-500', dot: 'bg-slate-400', icon: 'text-slate-500 bg-slate-100' },
  teal: { text: 'text-teal-700', dot: 'bg-teal-500', icon: 'text-teal-600 bg-teal-50' },
};
export const GROUPS = [
  { key: 'mentions', label: 'Mentions' },
  { key: 'failed', label: 'Failed / NSF' },
  { key: 'overdue', label: 'Overdue rent' },
  { key: 'disputes', label: 'Open disputes' },
  { key: 'tenants', label: 'Onboarding & consent' },
  { key: 'reports', label: 'Monthly reports' },
  { key: 'properties', label: 'Properties' },
];
const STORE = 'tenantra.attention.reviewed';
const names = (raw) => { const list = [...new Set(raw.filter(Boolean))]; return list.length <= 2 ? list.join(' and ') : `${list.slice(0, 2).join(', ')} and ${list.length - 2} more`; };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const money = (v) => `$${Number(v || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const reason = (p) => /insufficient|non sufficient|nsf/i.test(p.failure_reason || '') ? 'NSF' : p.status;
const item = (it) => ({ ...it, count: plural(it.n, ...it.unit) });

const KIND = { tenant: 'tenant', property: 'property', applicant: 'applicant' };
const mentionItems = (mn, openTenant) => (mn.mentions || []).map(m => item({
  id: `mention-${m.id}`, group: 'mentions', tone: 'teal', icon: AtSign, n: 1, unit: ['mention', 'mentions'],
  title: `${m.author_name} mentioned you on ${KIND[m.entity_type]} ${m.entity_label}`, detail: m.snippet,
  action: 'Open note', go: () => (m.entity_type === 'tenant' ? openTenant(m.entity_id) : navigate(m.link)),
}));

const STUCK = { lease: 'said the lease details are wrong', charges: 'said the move-in amounts are wrong', id_mismatch: "ID didn't match the application" };

function paymentItems(pa) {
  const items = [];
  const failed = pa.failed || [];
  if (failed.length) items.push(item({
    id: 'failed', group: 'failed', tone: 'red', icon: XCircle, n: failed.length, unit: ['payment', 'payments'],
    title: failed.length === 1 ? 'A rent payment bounced' : 'Rent payments bounced',
    detail: failed.map(p => `${p.tenant_name} ${money(p.amount)} (${reason(p)})`).join('; '),
    action: 'View payments', go: () => navigate('/landlord/payments'),
  }));
  const od = pa.overdue || [];
  if (od.length) items.push(item({
    id: `overdue-${pa.month}`, group: 'overdue', tone: 'amber', icon: Banknote, n: od.length, unit: ['tenant', 'tenants'],
    title: `${monthLabel(pa.month)} rent not received`,
    detail: `${names(od.map(o => o.tenant_name))}. ${money(od.reduce((s, o) => s + Math.max(o.rent - o.paid, 0), 0))} outstanding`,
    action: 'View payments', go: () => navigate('/landlord/payments'),
  }));
  return items;
}

function onboardingItems(att, openTenant) {
  const items = [];
  const stuck = att.onboarding_stuck || [];
  if (stuck.length) items.push(item({
    id: 'onboarding', group: 'tenants', tone: 'red', icon: UserCheck, n: stuck.length, unit: ['applicant', 'applicants'],
    title: 'Onboarding stopped', detail: stuck.map(s => `${s.name} (Unit ${s.unit_number}) ${STUCK[s.reason] || 'stopped'}`).join('; '),
    action: 'Review', go: () => navigate(stuck.length === 1 ? `/landlord/screening?property=${stuck[0].property_id}&open=${stuck[0].id}` : '/landlord/screening'),
  }));
  const rm = att.roommates_pending || [];
  if (rm.length) items.push(item({
    id: 'roommates', group: 'tenants', tone: 'amber', icon: UserCheck, n: rm.length, unit: ['roommate', 'roommates'],
    title: 'Roommate ID pending', detail: rm.map(r => `${r.roommate_name} (Unit ${r.unit_number}, ${r.street})`).join('; '),
    action: 'Follow up', go: () => openTenant(rm[0].tenant_id),
  }));
  return items;
}

function buildItems({ props, tenancies, reports, disputes }, openTenant) {
  const items = [];
  const open = disputes.filter(d => d.status === 'open');
  if (open.length) items.push(item({
    id: 'disputes', group: 'disputes', tone: 'red', icon: AlertTriangle, n: open.length, unit: ['dispute', 'disputes'],
    title: 'Waiting for your response', detail: `${names(open.map(d => d.tenant_name))} disputed a reported month`,
    action: 'Respond', go: () => navigate(open.length === 1 ? `/landlord/disputes/${open[0].id}` : '/landlord/disputes?status=open'),
  }));
  const reviewing = disputes.filter(d => d.status === 'under_review');
  if (reviewing.length) items.push(item({
    id: 'reviewing-disputes', group: 'disputes', tone: 'slate', icon: AlertTriangle, n: reviewing.length, unit: ['dispute', 'disputes'],
    title: 'Response sent, under review', detail: `${names(reviewing.map(d => d.tenant_name))}. We’ll let you know the outcome.`,
    action: 'View', go: () => navigate(reviewing.length === 1 ? `/landlord/disputes/${reviewing[0].id}` : '/landlord/disputes?status=under_review'),
  }));

  const period = lastMonthStart();
  const tenantName = Object.fromEntries(tenancies.map(t => [t.id, t.tenant_name]));
  const late = reports.filter(r => r.report_period?.start === period && ['late', 'missed', 'partial'].includes(r.payment?.status));
  if (late.length) items.push(item({
    id: `late-${period}`, group: 'overdue', tone: 'amber', icon: Banknote, n: late.length, unit: ['tenant', 'tenants'],
    title: `Late or missed rent reported for ${monthLabel(period)}`, detail: names(late.map(r => r.tenant_name || tenantName[r.tenancy_id])),
    action: late.length === 1 ? 'View history' : 'View reports', go: () => late.length === 1 ? openTenant(late[0].tenant_id) : navigate('/landlord/reports'),
  }));

  const reported = new Set(reports.filter(r => r.report_period?.start === period).map(r => r.tenancy_id));
  const due = tenancies.filter(t => t.status === 'active' && t.move_in_date <= period.slice(0, 8) + '28' && !reported.has(t.id));
  if (due.length) items.push(item({
    id: `unreported-${period}`, group: 'reports', tone: 'amber', icon: CalendarClock, n: due.length, unit: ['tenancy', 'tenancies'],
    title: `${monthLabel(period)} not reported yet`, detail: names(due.map(t => t.tenant_name)),
    action: 'Submit report', go: () => navigate(due.length === 1 ? `/landlord/reports/${due[0].id}/new` : '/landlord/reports/new'),
  }));

  const consent = tenancies.filter(t => t.status === 'pending_consent');
  if (consent.length) items.push(item({
    id: 'consent', group: 'tenants', tone: 'amber', icon: FileSignature, n: consent.length, unit: ['tenant', 'tenants'],
    title: 'Haven’t signed reporting consent', detail: `${names(consent.map(t => t.tenant_name))}. Nothing is reported until they sign.`,
    action: 'View tenants', go: () => navigate('/landlord/tenants?status=pending_consent'),
  }));

  const needsDocs = props.filter(p => p.verification_status === 'needs_documents');
  if (needsDocs.length) items.push(item({
    id: 'needs-docs', group: 'properties', tone: 'amber', icon: Home, n: needsDocs.length, unit: ['property', 'properties'],
    title: 'Ownership documents needed', detail: names(needsDocs.map(p => p.address.street)),
    action: 'Upload documents', go: () => navigate(needsDocs.length === 1 ? `/landlord/properties/${needsDocs[0].id}/verify` : '/landlord/properties?status=needs_documents'),
  }));
  const review = props.filter(p => ['pending', 'manual_review', 'incomplete'].includes(p.verification_status));
  if (review.length) items.push(item({
    id: 'pending-review', group: 'properties', tone: 'slate', icon: Home, n: review.length, unit: ['property', 'properties'],
    title: 'Verification in review', detail: `${names(review.map(p => p.address.street))}. We’ll notify you when it’s done.`,
    action: 'View properties', go: () => navigate('/landlord/properties?status=pending'),
  }));
  return items;
}

const EVT = 'tenantra-attention';
const readReviewed = () => JSON.parse(localStorage.getItem(STORE) || '[]');
const order = (it) => GROUPS.findIndex(g => g.key === it.group);
export const groupCounts = (list) => list.reduce((m, it) => ({ ...m, [it.group]: (m[it.group] || 0) + it.n }), {});
const toneOf = (list, key) => ['red', 'amber', 'teal', 'slate'].find(t => list.some(i => i.group === key && i.tone === t)) || 'slate';
let extras = null;
const soft = (p) => p.then(r => r.data).catch(() => ({}));
const loadExtras = () => {
  if (!extras || Date.now() - extras.at > 15000) extras = { at: Date.now(), promise: Promise.all([soft(api.get('/onboarding/attention')), soft(api.get('/landlord/payment-alerts')), soft(api.get('/landlord/notes/mentions'))]) };
  return extras.promise;
};

export function useAttention(enabled = true) {
  const [items, setItems] = useState(null);
  const [reviewed, setReviewed] = useState(readReviewed);
  const [tenantId, setTenantId] = useState(null);
  useEffect(() => {
    if (!enabled) { setItems([]); return; }
    Promise.all([loadHomeData(), loadExtras()])
      .then(([d, [att, pa, mn]]) => setItems([...mentionItems(mn, setTenantId), ...paymentItems(pa), ...onboardingItems(att, setTenantId), ...buildItems(d, setTenantId)]
        .map(i => ({ ...i, sig: `${i.id}:${i.detail}` })).sort((a, b) => order(a) - order(b))))
      .catch(() => setItems([]));
  }, [enabled]);
  useEffect(() => { const h = () => setReviewed(readReviewed()); window.addEventListener(EVT, h); return () => window.removeEventListener(EVT, h); }, []);
  const markReviewed = useCallback((it) => {
    const next = [...readReviewed().filter(s => !s.startsWith(`${it.id}:`)), it.sig];
    localStorage.setItem(STORE, JSON.stringify(next)); window.dispatchEvent(new Event(EVT));
  }, []);
  const list = items || [];
  return { loading: !items, items: list, open: list.filter(i => !reviewed.includes(i.sig)), isReviewed: (i) => reviewed.includes(i.sig), markReviewed, tenantId, setTenantId };
}

function ItemRow({ it, done, onReview, onGo, compact }) {
  const t = TONE[it.tone]; const Icon = it.icon; const key = it.group === 'mentions' ? it.id : it.id.split('-')[0];
  return (
    <li className={`flex gap-3 ${compact ? 'px-4 py-2.5' : 'px-5 py-3.5'} hover:bg-slate-50/70 transition-colors`} data-testid={`attention-${key}`}>
      <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${t.icon}`}><Icon className="w-3.5 h-3.5" /></span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <p className={`text-sm ${compact ? 'line-clamp-2' : 'truncate'} ${done ? 'text-slate-500' : 'text-slate-800'}`}>{it.title}</p>
          <span className="text-[11px] flex-shrink-0 text-slate-400 tabular-nums">{it.count}</span>
        </div>
        <p className={`text-xs text-slate-500 mt-0.5 ${compact ? 'line-clamp-2' : ''}`}>{it.detail}</p>
        <div className="flex items-center gap-4 mt-1.5">
          <button onClick={() => { onGo?.(); it.go(); }} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1 group" data-testid={`attention-action-${key}`}>
            {it.action} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </button>
          {done ? <span className="text-[11px] text-slate-400">Reviewed</span>
            : <button onClick={() => onReview(it)} className="text-[11px] text-slate-400 hover:text-slate-600" data-testid={`attention-review-${key}`}>Mark reviewed</button>}
        </div>
      </div>
    </li>
  );
}

function GroupedList({ list, compact, testPrefix, isDone = () => false, onReview, onGo }) {
  const counts = groupCounts(list);
  return GROUPS.filter(g => counts[g.key]).map(g => (
    <section key={g.key} data-testid={`${testPrefix}-section-${g.key}`}>
      <div className={`flex items-center gap-2 ${compact ? 'px-4' : 'px-5'} pt-3 pb-1`}>
        <span className={`w-1.5 h-1.5 rounded-full ${TONE[toneOf(list, g.key)].dot}`} />
        <span className="text-[11px] font-semibold text-slate-500">{g.label}</span>
        <span className="text-[11px] text-slate-400 tabular-nums">{counts[g.key]}</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {list.filter(it => it.group === g.key).map(it => <ItemRow key={it.id} it={it} compact={compact} done={isDone(it)} onReview={onReview} onGo={onGo} />)}
      </ul>
    </section>
  ));
}

function GroupChips({ list, value, onChange, testPrefix }) {
  const counts = groupCounts(list);
  const chips = [['all', 'All', null], ...GROUPS.filter(g => counts[g.key]).map(g => [g.key, g.label, counts[g.key]])];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-groups`}>
      {chips.map(([k, label, n]) => (
        <button key={k} onClick={() => onChange(k)} className={`h-7 px-2.5 rounded-full text-xs whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${value === k ? 'bg-teal-50 text-teal-800 font-medium' : 'bg-slate-100/70 text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`} data-testid={`${testPrefix}-group-${k}`}>
          {label}{n != null && <span className={`tabular-nums ${value === k ? 'text-teal-700' : 'text-slate-400'}`}>{n}</span>}
        </button>
      ))}
    </div>
  );
}

export const ActionRequired = () => {
  const { open } = useAttention();
  const counts = groupCounts(open);
  const groups = GROUPS.filter(g => counts[g.key]);
  if (!groups.length) return null;
  return (
    <section className="mb-6 bg-white border border-slate-200 rounded-lg" data-testid="action-required-block">
      <div className="flex items-center justify-between px-5 pt-4">
        <h2 className="text-sm font-semibold text-slate-800">Needs attention <span className="ml-1 font-normal text-slate-400" data-testid="attention-count">{plural(open.length, 'alert', 'alerts')}</span></h2>
        <button onClick={() => navigate('/landlord/notifications')} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1 group" data-testid="attention-view-all">
          View all <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1 p-2">
        {groups.map(g => (
          <button key={g.key} onClick={() => navigate(`/landlord/notifications?group=${g.key}`)} className="text-left rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors basis-[calc(50%-0.25rem)] sm:basis-auto sm:min-w-[150px] sm:pr-10" data-testid={`attention-group-${g.key}`}>
            <span className="flex items-center gap-1.5 text-xs text-slate-500 truncate"><span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TONE[toneOf(open, g.key)].dot}`} />{g.label}</span>
            <span className="mt-0.5 block text-xl font-semibold text-slate-800 tabular-nums" data-testid={`attention-group-count-${g.key}`}>{counts[g.key]}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

export const AttentionBell = ({ enabled }) => {
  const a = useAttention(enabled);
  const [pop, setPop] = useState(false);
  const [group, setGroup] = useState('all');
  const g = groupCounts(a.open)[group] ? group : 'all';
  const list = g === 'all' ? a.open : a.open.filter(i => i.group === g);
  return (
    <>
      <Popover open={pop} onOpenChange={(v) => { setPop(v); if (!v) setGroup('all'); }}>
        <PopoverTrigger asChild>
          <button className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 relative" aria-label="Notifications" data-testid="notification-bell">
            <Bell className="w-4 h-4" />
            {a.open.length > 0 && <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center tabular-nums border border-white" data-testid="notification-badge">{a.open.length}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[380px] max-w-[calc(100vw-1.5rem)] p-0 bg-white" data-testid="notification-dropdown">
          <div className="px-4 pt-3 pb-2.5 border-b border-slate-100 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-800">Notifications</p>
              <span className="text-[11px] text-slate-500">{a.open.length ? `${a.open.length} open` : 'All clear'}</span>
            </div>
            {a.open.length > 0 && <GroupChips list={a.open} value={g} onChange={setGroup} testPrefix="bell" />}
          </div>
          {a.open.length === 0 ? (
            <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-500" data-testid="notification-empty"><CheckCircle2 className="w-4 h-4 text-teal-600" /> You’re all caught up</div>
          ) : <div className="max-h-[380px] overflow-y-auto pb-1"><GroupedList list={list} compact testPrefix="bell" onReview={a.markReviewed} onGo={() => setPop(false)} /></div>}
          <button onClick={() => { setPop(false); navigate('/landlord/notifications'); }} className="w-full px-4 py-2.5 border-t border-slate-100 text-xs font-medium text-teal-700 hover:bg-slate-50 text-center" data-testid="notification-see-all">See all notifications</button>
        </PopoverContent>
      </Popover>
      <TenantPanel tenantId={a.tenantId} onClose={() => a.setTenantId(null)} />
    </>
  );
};

export function NotificationsPage() {
  const a = useAttention();
  const [tab, setTab] = useState('open');
  const [group, setGroup] = useUrlParam('group', 'all');
  const base = tab === 'open' ? a.open : a.items.filter(a.isReviewed);
  const stale = !a.loading && group !== 'all' && !groupCounts(base)[group];
  useEffect(() => { if (stale) setGroup('all'); }, [stale]); // eslint-disable-line
  const list = group === 'all' || stale ? base : base.filter(i => i.group === group);
  return (
    <div className="space-y-4" data-testid="notifications-page">
      <h1 className="text-[15px] font-medium text-slate-800">Notifications</h1>
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-100 space-y-3">
          <div className="inline-flex rounded-full bg-slate-100/70 p-0.5 text-xs" data-testid="notifications-tabs">
            {[['open', `Open (${a.open.length})`], ['reviewed', `Reviewed (${a.items.length - a.open.length})`]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`h-7 px-3 rounded-full transition-colors ${tab === k ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-testid={`notifications-tab-${k}`}>{l}</button>
            ))}
          </div>
          {base.length > 0 && <GroupChips list={base} value={stale ? 'all' : group} onChange={setGroup} testPrefix="notifications" />}
        </div>
        {a.loading ? <div className="p-5"><div className="h-12 bg-slate-100 rounded animate-pulse" /></div>
          : list.length === 0 ? <div className="px-5 py-8 flex items-center gap-2 text-sm text-slate-500" data-testid="notifications-empty"><CheckCircle2 className="w-4 h-4 text-teal-600" />{tab === 'open' ? 'You’re all caught up' : 'Nothing reviewed yet'}</div>
          : <div className="pb-1"><GroupedList list={list} testPrefix="notifications" isDone={a.isReviewed} onReview={a.markReviewed} /></div>}
      </div>
      <TenantPanel tenantId={a.tenantId} onClose={() => a.setTenantId(null)} />
    </div>
  );
}
